import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AppleIcon from '@mui/icons-material/Apple';
import { showInstallPrompt, isStandalone } from '../../utils/pwa';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    deferredPrompt?: BeforeInstallPromptEvent;
  }
}

const InstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (isStandalone()) {
      return;
    }

    // Check if user previously dismissed
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        // Don't show again for 7 days
        return;
      }
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const android = /android/.test(userAgent);
    
    setIsIOS(ios);
    setIsAndroid(android);

    // For Android Chrome, wait for beforeinstallprompt event
    if (android) {
      const handleBeforeInstall = (e: Event) => {
        e.preventDefault();
        window.deferredPrompt = e as BeforeInstallPromptEvent;
        // Show prompt after 30 seconds of usage
        setTimeout(() => {
          if (!dismissed) {
            setShowPrompt(true);
          }
        }, 30000);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstall);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }

    // For iOS, show manual instructions after 30 seconds
    if (ios) {
      setTimeout(() => {
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 30000);
    }
  }, [dismissed]);

  const handleInstall = async () => {
    if (isAndroid && window.deferredPrompt) {
      showInstallPrompt();
      setShowPrompt(false);
    }
    // For iOS, the dialog stays open to show instructions
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleNeverShow = () => {
    setShowPrompt(false);
    setDismissed(true);
    // Set a far future date to effectively never show again
    localStorage.setItem('pwa-install-dismissed', (Date.now() + 365 * 24 * 60 * 60 * 1000).toString());
  };

  if (!showPrompt || isStandalone()) {
    return null;
  }

  return (
    <Dialog
      open={showPrompt}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {isIOS ? (
              <AppleIcon color="primary" />
            ) : (
              <PhoneAndroidIcon color="primary" />
            )}
            <Typography variant="h6" component="span">
              Install Hostel Manager
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleDismiss}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Install our app for a better experience:
        </Typography>

        <Box component="ul" sx={{ pl: 2, mt: 2, mb: 2 }}>
          <Typography component="li" variant="body2" gutterBottom>
            ⚡ Faster loading and better performance
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            📱 Works offline - access data without internet
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            🔔 Get instant rent reminder notifications
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            🏠 Quick access from your home screen
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            💾 Uses less storage than a native app
          </Typography>
        </Box>

        {isIOS && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              How to install on iPhone/iPad:
            </Typography>
            <Typography variant="body2" component="div">
              1. Tap the <strong>Share</strong> button (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  verticalAlign: 'middle',
                }}
              >
                ⬆️
              </Box>
              ) at the bottom of Safari
              <br />
              2. Scroll down and tap <strong>"Add to Home Screen"</strong>
              <br />
              3. Tap <strong>"Add"</strong> in the top-right corner
            </Typography>
          </Alert>
        )}

        {isAndroid && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Click the "Install" button below to add Hostel Manager to your home screen.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleNeverShow} size="small" color="inherit">
          Don't show again
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleDismiss} size="small">
          Maybe later
        </Button>
        {isAndroid && window.deferredPrompt && (
          <Button
            onClick={handleInstall}
            variant="contained"
            startIcon={<GetAppIcon />}
            size="small"
          >
            Install
          </Button>
        )}
        {isIOS && (
          <Button
            onClick={handleDismiss}
            variant="contained"
            size="small"
          >
            Got it
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstallPrompt;
